import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import Loading from "~/components/Loading";
import { ManifoldNextGenAppHeader } from "~/components/ManifoldNextGenAppHeader";
import { NextGenSwitchPrompt } from "~/components/NextGenSwitchPrompt";
import useNextGenHeader, { NextGenHeaderProvider } from "~/contexts/nextgen-header";
import { PageContextProvider } from "~/contexts/page-context";
import { useAppearanceAnalytics } from "~/hooks/useAppearanceAnalytics";
import {
  useForceManifoldNextGenFeature,
  useManifoldNextGenFeature,
} from "~/hooks/useManifoldNextGenFeature";
import { useNextGenTheme, type NextGenTheme } from "~/hooks/useNextGenTheme";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { cn } from "~/lib/utils";
import { ROUTE_404 } from "~/router/constants";

export interface NextGenLayoutContext extends NextGenTheme {
  // Undefined when the user is locked into the new experience (no legacy fallback to switch to).
  onSwitchToClassic?: () => void;
}

export function ManifoldNextGenLayout() {
  const feature = useManifoldNextGenFeature();
  const forceFeature = useForceManifoldNextGenFeature();
  const [preferences, updatePreferences] = useUserPreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useNextGenTheme();
  useAppearanceAnalytics(theme);

  // The pathname this layout instance first rendered at. This layout mounts
  // exactly once when the user enters the /nova/* route tree and never
  // remounts while navigating between nextgen pages, so a change away from
  // that pathname means the user actually navigated within nextgen — as
  // opposed to landing here directly (typed URL, bookmark, refresh, or a link
  // from outside nextgen), where there's no in-app "back" destination.
  const [initialPathname] = useState(() => location.pathname);
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    if (location.pathname !== initialPathname) {
      setCanGoBack(true);
    }
  }, [location.pathname, initialPathname]);

  useEffect(() => {
    document.body.dataset.theme = "manifold-nextgen";
    return () => {
      delete document.body.dataset.theme;
    };
  }, []);

  if (feature.isLoading || forceFeature.isLoading) {
    return <Loading />;
  }

  if (!feature.enabled && !forceFeature.enabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  const isLockedIntoNextGen = forceFeature.enabled;

  // Eligible for the new experience but not yet opted in: offer to switch
  // rather than dead-ending the link at a 404. Users locked into the new
  // experience skip this prompt entirely — there is no legacy fallback for them.
  if (!isLockedIntoNextGen && !preferences.optedIntoManifoldNextGen) {
    return (
      <NextGenSwitchPrompt
        onConfirm={() => updatePreferences({ optedIntoManifoldNextGen: true })}
        onCancel={() => navigate(ROUTE_404, { replace: true })}
      />
    );
  }

  const handleSwitchToClassic = () => {
    updatePreferences({ optedIntoManifoldNextGen: false });
    navigate("/");
  };

  const onSwitchToClassic = isLockedIntoNextGen ? undefined : handleSwitchToClassic;

  return (
    <NextGenHeaderProvider>
      <PageContextProvider>
        <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
          <NextGenShell
            header={
              <ManifoldNextGenAppHeader
                onSwitchToClassic={onSwitchToClassic}
                canGoBack={canGoBack}
                resolvedTheme={theme.resolvedTheme}
              />
            }
          >
            <Outlet context={{ ...theme, onSwitchToClassic } satisfies NextGenLayoutContext} />
          </NextGenShell>
        </QueryParamProvider>
      </PageContextProvider>
    </NextGenHeaderProvider>
  );
}

/**
 * The header above the page. Normally only the page scrolls and the header stays
 * put; a colored header carries the Home hero, so the whole column scrolls and
 * the hero leaves with the page. Only the scrolling element changes, so
 * switching variants never remounts the header.
 */
const NextGenShell = ({ header, children }: { header: ReactNode; children: ReactNode }) => {
  const { variant } = useNextGenHeader();
  const heroScrolls = variant === "colored";
  return (
    <div
      data-testid="nextgen-shell"
      className={cn(
        "flex h-screen flex-col bg-background text-foreground",
        heroScrolls && "overflow-auto"
      )}
    >
      {header}
      <main className={cn("flex-1", !heroScrolls && "overflow-auto")}>{children}</main>
    </div>
  );
};
